import { useContact } from '@/context/Contact';
import { PortalRegistrationStatuses } from '@/context/Contact/useUserRegistrationStatus';
import { usePageModels } from '@/context/PageModels';
import ContentContainer from '@/features/shared/ContentContainer';
import { MuiContainer } from '@/features/shared/material-ui';
import { IS_PREVIEW, SpacingSizes } from '@/utils/constants';
import { resolveImageUrl } from '@/utils/resolvers';
import { get, pathOr } from 'lodash/fp';
import React, { useMemo } from 'react';
import { IUnauthorizedMessageProps } from '../UnauthorizedMessage.interfaces';

const getFirstMessageGetter = (labels: any) => (prev: string) =>
  get(`${prev}FirstMessageLabel`, labels);

const getSecondMessageGetter = (labels: any) => (prev: string) =>
  get(`${prev}SecondMessageLabel`, labels);

const getActionButtonGetter = (labels: any) => (prev: string) =>
  get(`${prev}ActionButtonLabel`, labels);

const keyMap: any = {
  [PortalRegistrationStatuses.DENIED]: 'userRegistrationDenied',
  [PortalRegistrationStatuses.PENDING]: 'userRegistrationPending',
  [PortalRegistrationStatuses.REGISTERED]: 'userRegistrationRegistered',
};

const withAuthorizationGlobalData =
  (AuthorizationMessageComponent: any) =>
  ({ variant = 'page' }: { variant?: 'header' | 'page' }) => {
    const models = usePageModels();
    const { registrationStatus } = useContact();

    const errorLabels = React.useMemo(() => pathOr({}, ['labels', 'errors'], models), [models]);

    const getFirstMessage = React.useMemo(() => getFirstMessageGetter(errorLabels), [errorLabels]);
    const getSecondMessage = React.useMemo(
      () => getSecondMessageGetter(errorLabels),
      [errorLabels],
    );
    const getActionButton = React.useMemo(() => getActionButtonGetter(errorLabels), [errorLabels]);

    const keyPrefix = React.useMemo(
      () => keyMap[registrationStatus.statusCode] || 'unauthorized',
      [registrationStatus.statusCode],
    );

    const firstMessageLabel = React.useMemo(
      () => getFirstMessage(keyPrefix),
      [getFirstMessage, keyPrefix],
    );
    const secondMessageLabel = React.useMemo(
      () => getSecondMessage(keyPrefix),
      [getSecondMessage, keyPrefix],
    );
    const actionButtonLabel = React.useMemo(
      () => getActionButton(keyPrefix),
      [getActionButton, keyPrefix],
    );

    const severity = React.useMemo(() => {
      switch (registrationStatus.statusCode) {
        case PortalRegistrationStatuses.DENIED:
          return 'error';
        case PortalRegistrationStatuses.PENDING:
          return 'warning';
        case PortalRegistrationStatuses.REGISTERED:
          return 'info';
        default:
          return 'error';
      }
    }, [registrationStatus.statusCode]);

    const {
      config: { sso: sooConfig = {} },
    } = usePageModels();

    const { ssoUnauthorizedWarningIcon, ssoUnauthorizedWarningActionUrl } = sooConfig;

    const iconUrl = useMemo(
      () => resolveImageUrl({ raw: ssoUnauthorizedWarningIcon }),
      [ssoUnauthorizedWarningIcon],
    );

    const componentProps: IUnauthorizedMessageProps = {
      iconUrl,
      firstMessageLabel,
      secondMessageLabel,
      actionButtonLabel,
      variant,
      severity,
      actionButtonUrl: ssoUnauthorizedWarningActionUrl,
    };

    if (IS_PREVIEW) return null;

    if (variant === 'page')
      return (
        <ContentContainer topSpacing={SpacingSizes.small} bottomSpacing={SpacingSizes.small}>
          <MuiContainer className="medium">
            <AuthorizationMessageComponent {...componentProps} />
          </MuiContainer>
        </ContentContainer>
      );

    return <AuthorizationMessageComponent {...componentProps} />;
  };

export default withAuthorizationGlobalData;
